/* Bimini Finder — Brand → Model → the bimini lines that fit, sized to the boat's mounting
   width, with live market-scoped pricing. Reads assets/bimini-finder-data.json
   ({ lines[], manufacturers: { brand: [ { model, range, boatType, length, beam, fits } ] } }).
   For every line the boat fits, the product is fetched live by handle (Storefront API
   @inContext) and the variants whose SKU starts with the width code ("MA 063-3") are the
   ones offered — colour swatches and option pills (rocket launcher, fishing arch) come from
   the product's own variants, so nothing about widths, colours or prices is baked in.
   Initialises every .bmf instance on the page. No dependencies. */
(function () {
  "use strict";

  // After an add, open the theme's cart drawer (it rebuilds the header count and
  // drawer contents itself) by triggering the header cart icon's own handler.
  // Without a drawer (cart page mode), just refresh the count and contents.
  function refreshCart() {
    var opener = document.querySelector(".openCartDrawer");
    if (opener && window.jQuery) { opener.click(); return; }
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (window.buildCart && window.jQuery) { window.buildCart(cart, false); return; }
        document.querySelectorAll("[data-cart-count]").forEach(function (n) {
          n.textContent = cart.item_count < 100 ? cart.item_count : "";
          n.style.display = cart.item_count > 0 ? "" : "none";
        });
      })
      .catch(function () {});
  }

  function init(root) {
    if (root.dataset.bmfReady) return;
    root.dataset.bmfReady = "1";

    var market = window.OS_MARKET || {};
    var token = root.dataset.token || "";
    var country = String(market.country || root.dataset.country || "AU")
      .toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2) || "AU";
    var rootUrl = (market.root || root.dataset.rootUrl || "/").replace(/\/+$/, "") + "/";
    var apiVersion = root.dataset.apiVersion || "2024-10";
    var dataSrc = root.dataset.src;
    var enquireUrl = root.dataset.enquireUrl || "/pages/contact";
    // US: widths and lengths in inches/feet, US spelling. Data stays in metres either way.
    var IMPERIAL = root.dataset.units === "imperial";
    var ALU_WORD = IMPERIAL ? "aluminum" : "aluminium";
    var popText = root.dataset.popText || "";   // empty = popularity switched off in the editor
    var popMin = (parseFloat(root.dataset.popMin) || 0) / 100; // bar + text hidden below this share
    var headingRec = root.dataset.headingRec || "Oceansouth recommends";
    var headingAlt = root.dataset.headingAlt || "Alternative options";
    var measureLabel = root.dataset.measureLabel || "Click for Dimensions";
    var addonsHeading = root.dataset.addonsHeading || "Optional Extras (tick to add)";
    var SAIL = handleList(root.dataset.sailHandles); // Sailboat / Yacht biminis (stainless, any width)
    function handleList(s) { return String(s || "").split(",").map(function (x) { return x.trim(); }).filter(Boolean); }
    var DIAGRAMS = {}; // line id -> measuring diagram (theme asset); else the product description's image
    try { DIAGRAMS = JSON.parse(root.dataset.diagrams || "{}"); } catch (e) { /* fall back to descriptions */ }

    var mfrSel = root.querySelector('[data-role="mfr"]');
    var mdlSel = root.querySelector('[data-role="model"]');
    var results = root.querySelector('[data-role="results"]');
    var hiddenNote = root.querySelector('[data-role="hidden-lines"]');
    var emptyNote = root.querySelector('[data-role="empty"]');
    var boatStrip = root.querySelector('[data-role="boat"]');
    var boatName = root.querySelector('[data-role="boat-name"]');
    var boatWidth = root.querySelector('[data-role="boat-width"]');
    var fabricsWrap = root.querySelector('[data-role="fabrics"]');
    var fabricsList = root.querySelector('[data-role="fabrics-list"]');
    var widthToggle = root.querySelector('[data-role="width-toggle"]');
    var widthForm = root.querySelector('[data-role="width-form"]');
    var measureBox = root.querySelector('[data-role="measure"]');
    var widthError = root.querySelector('[data-role="width-error"]');
    // Results render below the fold; bring them into view when they change so the shopper sees them.
    function revealResults() {
      var target = boatStrip && !boatStrip.hidden ? boatStrip : results;
      if (!target) return;
      var r = target.getBoundingClientRect();
      if (r.top > window.innerHeight * 0.6 || r.top < 0) {
        window.scrollTo({ top: window.pageYOffset + r.top - 90, behavior: "smooth" });
      }
    }
    var widthInput = root.querySelector('[data-role="width"]');
    var typeBtns = Array.prototype.slice.call(root.querySelectorAll(".bmf__type"));
    var pickedType = "", pickedMode = ""; // mode: "" | "sail" | "other"
    var resetBtns = Array.prototype.slice.call(root.querySelectorAll('[data-role="reset"]'));
    var restartHint = root.querySelector('[data-role="restart-hint"]');
    // Boat Icon label -> the sheet's boat categories, for "owners of this boat type chose …".
    // Types with no matching category (inflatable, RIB) just get the fitting biminis unranked.
    // Open Boat = tinnies and punts too (Chris, 2026-10-14), so the Fishing Boat / Jon Boat tiles are left out.
    var OPEN = ["Open Boat", "Forked Open", "Traditional Tinny", "Traditional Tinny Wide Bow", "Wide Bow Tri-Hull Tinny", "Traditional Bow", "Pointy Nose",
                "Wide Bow Traditional Tinny", "Large Traditional Tinny", "Traditional Tinny Pointy Bow", "Square Nose Punt", "Punt", "Square Nose"];
    var TYPE_CATEGORIES = {
      "runabout": ["Runabout", "Runabout Style"],
      "ski boat": ["Bowrider", "Bowrider Style", "Runabout Style Bowrider", "Wide Bow Tri-Hull Bowrider"],
      "bowrider": ["Bowrider", "Bowrider Style", "Runabout Style Bowrider", "Wide Bow Tri-Hull Bowrider"],
      // US sheet labels too ("Centre Console Cover" is how the team's USA sheet writes it, 21 Oct 2026)
      "center console": ["Centre Console", "Rear Console", "Center Console", "Centre Console Cover"],
      "bass boat": ["Bass Boat"],
      "jon boat": ["Jon Boat"],
      "centre console": ["Centre Console", "Rear Console"],
      "open boat": OPEN,
      "fishing boat": OPEN,
      "side console": ["Side Console"],
      "half cabin": ["Half Cab", "Cuddy Cabin", "Centre Cab", "Centre Cabin"],
      "cabin cruiser": ["Half Cab", "Cuddy Cabin", "Centre Cab", "Centre Cabin"]
    };
    // Average "% of previous customers" per bimini line across every boat of the type's categories.
    function popularityForType(label) {
      var cats = TYPE_CATEGORIES[String(label || "").toLowerCase()];
      if (!cats || !DATA) return null;
      var sum = {}, n = 0;
      Object.keys(DATA.manufacturers).forEach(function (m) {
        DATA.manufacturers[m].forEach(function (b) {
          if (!b.popular || cats.indexOf(b.boatType) < 0) return;
          n++;
          Object.keys(b.popular).forEach(function (k) { sum[k] = (sum[k] || 0) + b.popular[k]; });
        });
      });
      if (!n) return null;
      var avg = {}; Object.keys(sum).forEach(function (k) { avg[k] = sum[k] / n; });
      return avg;
    }

    var DESCRIPTIONS = {};
    var descEl = root.querySelector('[data-role="descriptions"]');
    if (descEl) { try { DESCRIPTIONS = JSON.parse(descEl.textContent) || {}; } catch (e) { DESCRIPTIONS = {}; } }
    var BADGES = {};
    var badgeEl = root.querySelector('[data-role="badges"]');
    if (badgeEl) { try { BADGES = JSON.parse(badgeEl.textContent) || {}; } catch (e) { BADGES = {}; } }

    var DATA = null;
    var productCache = {}; // handle -> Promise<product|null>
    var current = null;    // { label, meta, fits, measured }

    function apiUrl() { return "/api/" + apiVersion + "/graphql.json"; }
    function numericId(gid) { return String(gid || "").split("/").pop(); }
    function productUrl(handle, varId) {
      var u = rootUrl + "products/" + handle;
      return varId ? u + "?variant=" + varId : u;
    }
    function formatPrice(money) {
      if (!money) return "";
      try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency: money.currencyCode }).format(parseFloat(money.amount));
      } catch (e) { return money.currencyCode + " " + money.amount; }
    }
    function el(tag, cls, text) {
      var e = document.createElement(tag);
      if (cls) e.className = cls;
      if (text != null) e.textContent = text;
      return e;
    }
    function normSku(s) { return String(s || "").replace(/\s+/g, "").toUpperCase(); }
    function inches(m) { return Math.round(m / 0.0254); }
    // Product option values are written per product: the US tops say 61" - 66", the shared AU
    // products 1.3m - 1.5m. For US shoppers, show metric ones in inches.
    function widthText(wv) {
      if (!IMPERIAL || !/\d\s*m\b/i.test(wv)) return wv;
      return String(wv).replace(/(\d+(?:\.\d+)?)\s*m\b\s*([-–]\s*)?/gi, function (_, n, dash) { return inches(parseFloat(n)) + "\u2033" + (dash ? " – " : ""); });
    }
    function fmtM(n) {
      if (!IMPERIAL) return (Math.round(n * 100) / 100).toString() + " m";
      var i = inches(n);
      return Math.floor(i / 12) + "\u2032 " + (i % 12) + "\u2033"; // 7′ 2″
    }

    function fetchProduct(handle) {
      if (productCache[handle]) return productCache[handle];
      var query =
        "query @inContext(country: " + country + ") { product(handle: " + JSON.stringify(handle) + ") { title descriptionHtml featuredImage { url(transform:{maxWidth:640}) altText } " +
        "variants(first: 100) { nodes { sku title availableForSale id price { amount currencyCode } " +
        "image { url(transform:{maxWidth:640}) altText } selectedOptions { name value } } } } }";
      var p = fetch(apiUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": token },
        body: JSON.stringify({ query: query }),
      })
        .then(function (r) { return r.json(); })
        .then(function (j) { return (j && j.data && j.data.product) || null; })
        .catch(function () { return null; });
      productCache[handle] = p;
      return p;
    }

    // ---- variant helpers ----
    function isColorOpt(o) { return /colou?r/i.test(o.name); }
    function isWidthOpt(o) { return /width/i.test(o.name); }
    function optValue(v, pred) {
      var so = v.selectedOptions || [], i;
      for (i = 0; i < so.length; i++) { if (pred(so[i])) return so[i].value; }
      return "";
    }
    function otherSig(v) { // options that are neither colour nor width (e.g. rocket launcher)
      var so = v.selectedOptions || [], parts = [], i;
      for (i = 0; i < so.length; i++) { if (!isColorOpt(so[i]) && !isWidthOpt(so[i])) parts.push(so[i].name + "=" + so[i].value); }
      return parts.sort().join("|");
    }
    function otherOption(v) { // the first non-colour, non-width option, if any
      var so = v.selectedOptions || [], i;
      for (i = 0; i < so.length; i++) { if (!isColorOpt(so[i]) && !isWidthOpt(so[i])) return so[i]; }
      return null;
    }
    // Variants whose SKU is the width code plus a colour suffix: "MA 063-3" → MA 063-3B, MA 063-3BK …
    function variantsForCode(variants, code) {
      var re = new RegExp("^" + normSku(code).replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "[A-Z]*$");
      return variants.filter(function (v) { return re.test(normSku(v.sku)); });
    }
    // Swatch order the team asked for (Joe, 21 Oct 2026); colours not listed (e.g. White) follow.
    var COLOR_ORDER = [/navy|^blue$/, /black/, /^gr[ae]y$/, /dark gr[ae]y/, /sand|beige/, /pacific/, /maroon|burgundy/];
    function colorRank(name) {
      var n = (name || "").toLowerCase().trim(), i;
      for (i = 0; i < COLOR_ORDER.length; i++) { if (COLOR_ORDER[i].test(n)) return i; }
      return COLOR_ORDER.length;
    }
    function byColor(list, nameOf) { // stable: keeps the product's order within the same rank
      return list.map(function (v, i) { return { v: v, i: i, r: colorRank(nameOf(v)) }; })
        .sort(function (x, y) { return x.r - y.r || x.i - y.i; })
        .map(function (x) { return x.v; });
    }
    // US wording: what AU calls a rocket launcher is a fishing rod rack in the US (Joe, 22 Oct 2026).
    // The US store's variants say "Fishing Arch" (staging's copy still says "Rocket Launcher"); the
    // finder shows "Fishing Rod Rack" for either. Variant names themselves are unchanged.
    function localWords(t) { return IMPERIAL ? String(t).replace(/rocket launcher|fishing arch/gi, "Fishing Rod Rack") : t; }
    function colorHex(name) {
      var n = (name || "").toLowerCase();
      if (n.indexOf("black") >= 0) return "#1c1f24";
      if (n.indexOf("dark grey") >= 0 || n.indexOf("dark gray") >= 0) return "#4d5258";
      if (n.indexOf("grey") >= 0 || n.indexOf("gray") >= 0) return "#9aa3ad";
      if (n.indexOf("pacific") >= 0) return "#2a6fa8";
      if (n.indexOf("navy") >= 0 || n.indexOf("blue") >= 0) return "#1f2f5a";
      if (n.indexOf("white") >= 0) return "#eef0f2";
      if (n.indexOf("sand") >= 0 || n.indexOf("beige") >= 0) return "#d9c9a5";
      if (n.indexOf("maroon") >= 0 || n.indexOf("burgundy") >= 0) return "#6d1f2c";
      if (n.indexOf("green") >= 0) return "#3a5a40";
      return "#8a93a3";
    }

    // ---- population ----
    function populateManufacturers() {
      Object.keys(DATA.manufacturers).sort().forEach(function (n) {
        var o = el("option", null, n);
        o.value = n;
        mfrSel.appendChild(o);
      });
    }
    // "Range Model" (e.g. "Renegade 480") when the series isn't already in the model name; a bare
    // number gets the brand in front. Two boats with the same label are told apart by boat type.
    function modelLabel(rec, mfr) {
      var m = (rec.model || "").trim(), r = (rec.range || "").trim();
      if (r && m.toLowerCase().indexOf(r.toLowerCase()) === -1) return (r + " " + m).trim();
      if (!r && mfr && /^\d+(\.\d+)?$/.test(m)) return (mfr + " " + m).trim();
      return m;
    }
    // US data keeps the sheet's Model apart from its configuration and years (rec.base / config /
    // years): the drop-down lists each Model once, as the boat cover finder does, and a third step
    // asks for the configuration only when the configurations fit different biminis.
    function fitSig(rec) { return JSON.stringify([rec.fits || {}, rec.popular || null]); }
    function configLabel(rec) {
      return ((rec.config || "Standard") + (rec.years ? " (" + rec.years + ")" : "")).trim();
    }
    var cfgStep = null;
    function hideConfigs() { if (cfgStep) cfgStep.hidden = true; }
    function showConfigs(group, mfr) {
      if (!cfgStep) {
        cfgStep = el("div", "bmf__field bmf__configs");
        cfgStep.appendChild(el("span", "bmf__label", "3. Choose your configuration"));
        cfgStep.appendChild(el("div", "bmf__pills"));
        var controls = mdlSel.closest(".bmf__controls");
        controls.parentNode.insertBefore(cfgStep, controls.nextSibling);
      }
      var row = cfgStep.querySelector(".bmf__pills");
      row.innerHTML = "";
      var seen = {};
      group.recs.forEach(function (rec) { var l = configLabel(rec); seen[l] = (seen[l] || 0) + 1; });
      group.recs.forEach(function (rec) {
        var l = configLabel(rec);
        if (seen[l] > 1 && rec.boatType) l += " · " + rec.boatType;
        var b = el("button", "bmf__pill", l);
        b.type = "button";
        b.setAttribute("aria-pressed", "false");
        b.addEventListener("click", function () {
          Array.prototype.forEach.call(row.children, function (c) { c.setAttribute("aria-pressed", "false"); });
          b.setAttribute("aria-pressed", "true");
          resetDownstream();
          pickBoat(rec, mfr);
        });
        row.appendChild(b);
      });
      cfgStep.hidden = false;
    }
    function pickBoat(rec, mfr, label) {
      current = { fits: rec.fits, popular: rec.popular || null, measured: false, widthFromProduct: false };
      showBoat(rec, mfr, label);
      renderCards();
      revealResults();
    }
    function populateModels(mfr) {
      mdlSel.innerHTML = '<option value="">Select model…</option>';
      mdlSel._groups = null;
      var all = DATA.manufacturers[mfr] || [];
      if (all.length && all[0].base) {
        var groups = [], byBase = {};
        all.forEach(function (rec) {
          if (!byBase[rec.base]) { byBase[rec.base] = { label: rec.base, recs: [] }; groups.push(byBase[rec.base]); }
          byBase[rec.base].recs.push(rec);
        });
        groups.sort(function (a, b) { return a.label.localeCompare(b.label, undefined, { numeric: true }); });
        groups.forEach(function (g, i) {
          var o = el("option", null, g.label);
          o.value = String(i);
          mdlSel.appendChild(o);
        });
        mdlSel.disabled = false;
        mdlSel._groups = groups;
        mdlSel._recs = null;
        return;
      }
      var recs = (DATA.manufacturers[mfr] || []).slice().sort(function (a, b) {
        return modelLabel(a, mfr).localeCompare(modelLabel(b, mfr), undefined, { numeric: true });
      });
      var seen = {};
      recs.forEach(function (rec) { var l = modelLabel(rec, mfr); seen[l] = (seen[l] || 0) + 1; });
      recs.forEach(function (rec, i) {
        var label = modelLabel(rec, mfr);
        if (seen[label] > 1 && rec.boatType) label += " · " + rec.boatType;
        var o = el("option", null, label);
        o.value = String(i);
        mdlSel.appendChild(o);
      });
      mdlSel.disabled = recs.length === 0;
      mdlSel._recs = recs;
    }

    // ---- fit logic ----
    function lineById(id) {
      var ls = DATA.lines, i;
      for (i = 0; i < ls.length; i++) { if (ls[i].id === id) return ls[i]; }
      return null;
    }
    function fitsFromWidth(w) { // replay the sheet's bands for a typed mounting width
      var fits = {};
      DATA.lines.forEach(function (ln) {
        var code = null, i;
        for (i = 0; i < ln.bands.length; i++) {
          if (w >= ln.bands[i].lo && w <= ln.bands[i].hi) { code = ln.bands[i].code; break; }
        }
        fits[ln.id] = code;
      });
      return fits;
    }
    function fittingLines(fits, mat) {
      return DATA.lines.filter(function (ln) { return fits[ln.id] && (!mat || ln.material === mat); });
    }
    function bandLabel(fits) { // "1.9 – 2.09 m" from the first fitting line's band
      var ls = fittingLines(fits, null), i, j;
      for (i = 0; i < ls.length; i++) {
        for (j = 0; j < ls[i].bands.length; j++) {
          if (ls[i].bands[j].code === fits[ls[i].id]) {
            var b = ls[i].bands[j];
            return IMPERIAL ? inches(b.lo) + "\u2033 – " + inches(b.hi) + "\u2033" : b.lo + " – " + b.hi + " m";
          }
        }
      }
      return "";
    }

    // ---- rendering ----
    function eyebrowFor(line, measured) {
      if (line.eyebrow) return line.eyebrow;
      var parts = [line.bows + " bows", line.lengthLabel + " long", line.material === "stainless" ? "stainless steel frame" : ALU_WORD + " frame"];
      return (measured ? "Measured fit · " : "") + parts.join(" · ");
    }
    // The bold first line of a description ("More shade for longer days on the water").
    var TAGLINE_RE = /^\s*<p>\s*<strong>([^<]+)<\/strong>\s*<\/p>/i;
    function taglineFor(line) { var m = (DESCRIPTIONS[line.family] || "").match(TAGLINE_RE); return m ? m[1].trim() : ""; }
    // Badges on the card photo: an uploaded image, else custom text, else the product team's
    // artwork (Whitewater Pro has two), else the description's tagline. Stainless frames add the
    // 316 marine grade badge alongside.
    function fixUrl(u) { return String(u).replace(/^\/\//, "https://"); }
    function badgeFor(line) {
      var b = BADGES[line.family] || {}, out = null;
      if (b.img) out = { imgs: [fixUrl(b.img)] };
      else if ((b.text || "").trim()) out = { text: b.text.trim(), isTagline: false };
      else if (b.art && b.art.length) out = { imgs: b.art.map(fixUrl), isTagline: true };
      else { var t = taglineFor(line); if (t) out = { text: t, isTagline: true }; }
      if (line.material === "stainless" && BADGES._stainless) {
        out = out || {};
        out.extra = fixUrl(BADGES._stainless);
      }
      return out;
    }
    function descriptionFor(line, dropTagline) {
      var sail = /^sail_/.test(line.id || "");
      var d = (sail ? DESCRIPTIONS[/cruiser/i.test(line.handle) ? "Cruiser" : "Sailboat"] : DESCRIPTIONS[line.family]) || "";
      if (dropTagline) d = d.replace(TAGLINE_RE, "");
      if (line.material === "stainless" && !sail) { // the copy is written for the aluminium frame
        d = d.replace(/anodi[sz]ed alumini?um frame/gi, "polished 316 stainless steel frame");
        if (DESCRIPTIONS._stainless) d += "<p>" + DESCRIPTIONS._stainless + "</p>";
      }
      return d;
    }

    function buildCard(line, code, opts) {
      opts = opts || {};
      var compact = !!opts.compact;
      var card = el("div", "bmf__card bmf__card--loading" + (compact ? " bmf__card--compact" : ""));
      var media = el("div", "bmf__card-media");
      var body = el("div", "bmf__card-body");
      var eyebrow = el("p", "bmf__eyebrow", compact ? "Replacement fabric" : eyebrowFor(line, opts.measured));
      var title = el("h3", "bmf__card-title", line.title || line.family);
      var badge = compact ? null : badgeFor(line);
      var desc = el("div", "bmf__card-desc");
      if (!compact) desc.innerHTML = descriptionFor(line, badge && badge.isTagline); // theme-editor rich text
      var sizeEl = el("p", "bmf__size", "");
      var measureBtn = null;
      var optsWrap = el("div", "bmf__options");
      var colorsWrap = el("div", "bmf__colors");
      var addonWrap = el("div", "bmf__addon-wrap");
      var price = el("p", "bmf__price", "");
      var sku = el("p", "bmf__sku", "");
      sku.hidden = true;
      var actions = el("div", "bmf__actions");
      var view = el("a", "bmf__cta", compact ? "View fabric" : "View bimini");
      view.setAttribute("href", "#");
      actions.appendChild(view);

      body.appendChild(eyebrow);
      body.appendChild(title);
      if (!compact && popText && opts.popular > 0 && opts.popular >= popMin) {
        var pct = Math.round(opts.popular * 100);
        var pop = el("div", "bmf__pop" + (opts.recommended ? " bmf__pop--top" : ""));
        var bar = el("div", "bmf__pop-bar");
        bar.setAttribute("aria-hidden", "true");
        var fill = el("span", "bmf__pop-fill");
        fill.style.width = Math.max(pct, 3) + "%";
        bar.appendChild(fill);
        var txt = el("p", "bmf__pop-text");
        txt.appendChild(el("strong", "bmf__pop-pct", pct + "%"));
        txt.appendChild(document.createTextNode(" " + (opts.popText || popText)));
        pop.appendChild(bar);
        pop.appendChild(txt);
        body.appendChild(pop);
      }
      if (desc.innerHTML) body.appendChild(desc);
      body.appendChild(sizeEl);
      if (!compact) {
        measureBtn = el("button", "bmf__measure");
        measureBtn.type = "button";
        measureBtn.hidden = true; // shown once the product's diagram is known
        measureBtn.innerHTML = '<svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17 17 3l4 4L7 21z"/><path d="m7 13 2 2M10 10l2 2M13 7l2 2"/></svg>';
        measureBtn.appendChild(document.createTextNode(measureLabel));
        body.appendChild(measureBtn);
      }
      body.appendChild(optsWrap);
      body.appendChild(colorsWrap);
      body.appendChild(addonWrap);
      body.appendChild(price);
      body.appendChild(sku);
      body.appendChild(actions);

      var main = el("div", "bmf__card-main");
      main.appendChild(media);
      main.appendChild(body);
      card.appendChild(main);

      if (badge) {
        var badges = el("div", "bmf__badges");
        if (badge.text) {
          var blen = badge.text.length; // step the type down so long lines stay inside the disc
          var bdg = el("div", "bmf__badge" + (blen > 46 ? " bmf__badge--xlong" : blen > 34 ? " bmf__badge--long" : ""));
          bdg.appendChild(el("span", "bmf__badge-text", badge.text));
          badges.appendChild(bdg);
        }
        (badge.imgs || []).concat(badge.extra ? [badge.extra] : []).forEach(function (src) {
          var bd = el("div", "bmf__badge bmf__badge--img");
          var bi = new Image(); bi.src = src; bi.alt = ""; bi.decoding = "async";
          bd.appendChild(bi);
          badges.appendChild(bd);
        });
        if (badges.children.length) media.appendChild(badges);
      }

      // Aluminium | Stainless switch, top right, only when this boat has both for this bimini
      if (!compact && opts.materials && opts.materials.length > 1) {
        var sw = el("div", "bmf__mat");
        sw.setAttribute("role", "group");
        sw.setAttribute("aria-label", "Frame material");
        opts.materials.forEach(function (m) {
          var b = el("button", "bmf__mat-btn", m === "stainless" ? "Stainless steel" : (IMPERIAL ? "Aluminum" : "Aluminium"));
          b.type = "button";
          b.setAttribute("aria-pressed", String(m === line.material));
          b.addEventListener("click", function () { if (m !== line.material && opts.onSwitch) opts.onSwitch(m); });
          sw.appendChild(b);
        });
        sw.appendChild(el("span", "bmf__mat-beam")); // travelling light on the border (CSS)
        card.appendChild(sw);
        card.classList.add("bmf__card--has-mat");
      }

      if (line.unresolved || !line.handle) {
        renderEnquire(card, sizeEl, optsWrap, colorsWrap, price, view, code);
        return card;
      }

      fetchProduct(line.handle).then(function (product) {
        card.classList.remove("bmf__card--loading");
        if (!product) { // network/API failure: degrade to a plain PDP link
          optsWrap.remove(); colorsWrap.remove(); price.remove(); sizeEl.remove();
          view.setAttribute("href", productUrl(line.handle, null));
          return;
        }
        title.textContent = product.title || line.title || line.family;
        var variants = (product.variants && product.variants.nodes) || [];
        var base, widthMatched = false, widthVals = [];
        if (opts.anyWidth) {
          // Sailboat / cruiser biminis: no width code from the sheet — offer every width, and
          // preselect the one whose range ("2.7m - 3.0m") holds the width entered, if any.
          variants.forEach(function (v) { var wv = optValue(v, isWidthOpt); if (wv && widthVals.indexOf(wv) < 0) widthVals.push(wv); });
          widthVals.sort(function (a, b) { return parseFloat(a) - parseFloat(b); });
          var pickW = "";
          if (opts.width) widthVals.forEach(function (wv) {
            var m = String(wv).match(/([\d.]+)\s*m?\s*[-–]\s*([\d.]+)/);
            if (!pickW && m && opts.width >= parseFloat(m[1]) && opts.width <= parseFloat(m[2])) pickW = wv;
          });
          if (opts.requireFit && !pickW) { var sl = card.parentNode; card.remove(); if (sl && opts.onGone) opts.onGone(sl); return; }
          widthMatched = !!pickW;
          pickW = pickW || widthVals[0] || "";
          base = pickW ? variants.filter(function (v) { return optValue(v, isWidthOpt) === pickW; }) : variants;
          if (!desc.innerHTML) { // first paragraph of the product description stands in for the team copy
            var tmp = document.createElement("div"); tmp.innerHTML = product.descriptionHtml || "";
            var para = Array.prototype.filter.call(tmp.querySelectorAll("p"), function (x) { return x.textContent.trim().length > 40; })[0];
            if (para) { desc.appendChild(el("p", null, para.textContent.trim())); if (!desc.parentNode) body.insertBefore(desc, sizeEl); }
          }
        } else {
          base = variantsForCode(variants, code);
        }
        if (!base.length) { renderEnquire(card, sizeEl, optsWrap, colorsWrap, price, view, code); return; }
        // open on the first colour that is actually buyable; fall back to the first in the list
        base = byColor(base, function (v) { return optValue(v, isColorOpt); });
        var matched = base.filter(function (v) { return v.availableForSale && parseFloat((v.price || {}).amount || 0) > 0; })[0] || base[0];
        var widthVal = optValue(matched, isWidthOpt);
        // every variant of the product in the same width, across options and colours
        var sameWidth = widthVal ? variants.filter(function (v) { return optValue(v, isWidthOpt) === widthVal; }) : base;
        var cur = matched, addBtn = null, swatchRow = null;
        var addons = []; // { key, on, variant } — support poles, rod holder …
        var ADDON_ORDER = ["poles", "rod"];
        function addonsOn() {
          return addons.filter(function (a) { return a.on && a.variant; })
            .sort(function (x, y) { return ADDON_ORDER.indexOf(x.key) - ADDON_ORDER.indexOf(y.key); });
        }
        function btnLabel() {
          var on = addonsOn();
          if (!on.length) return "Add to cart";
          return on.length === 1 ? "Add bimini + " + on[0].short : "Add bimini + extras";
        }

        // measuring diagram: the line's own theme asset, else the (single) image in the product description
        var diag = (product.descriptionHtml || "").match(/<img[^>]+src="([^"]+)"/i);
        var diagSrc = DIAGRAMS[line.id] || (diag && diag[1].replace(/&amp;/g, "&"));
        if (measureBtn && diagSrc) {
          measureBtn.hidden = false;
          measureBtn.addEventListener("click", function () { openDiagram(diagSrc, title.textContent); });
        }

        function paint(variant) {
          cur = variant;
          var imgs = media.querySelectorAll("img.bmf__card-img"), k;
          for (k = imgs.length - 1; k >= 0; k--) { imgs[k].parentNode.removeChild(imgs[k]); }
          var img = variant.image || product.featuredImage;
          if (img && img.url) {
            var im = new Image(); im.loading = "lazy"; im.src = img.url;
            im.alt = title.textContent; im.className = "bmf__card-img";
            media.insertBefore(im, media.firstChild);
          }
          var wv = optValue(variant, isWidthOpt);
          sizeEl.textContent = !wv ? "" : opts.anyWidth
            ? "Mounting width " + widthText(wv) + (widthMatched && wv === optValue(matched, isWidthOpt) ? " — matches your measurement" : "")
            : "Mounting width " + widthText(wv) + (opts.measured ? " — matches your measurement" : " — selected for your boat");
          var amount = variant.price ? parseFloat(variant.price.amount) : 0;
          if (variant.availableForSale && amount > 0) {
            price.textContent = formatPrice(variant.price);
            price.className = "bmf__price";
            addonsOn().forEach(function (a) { price.appendChild(el("span", "bmf__price-addon", "+ " + formatPrice(a.variant.price) + " " + a.short)); });
            if (addBtn) { addBtn.disabled = false; addBtn.textContent = btnLabel(); }
          } else {
            price.textContent = amount > 0 ? "Currently unavailable" : "Price on request";
            price.className = "bmf__price bmf__price--soldout";
            if (addBtn) addBtn.disabled = true;
          }
          if (variant.sku) { sku.textContent = "SKU: " + variant.sku; sku.hidden = false; } else { sku.hidden = true; }
          view.setAttribute("href", productUrl(line.handle, numericId(variant.id)));
          if (!compact && !opts.measured && boatWidth && wv && current && !current.widthFromProduct) {
            current.widthFromProduct = true;
            boatWidth.textContent = "Your mounting width is " + widthText(wv) + ". Bimini frames mount inside the gunwale, so we size them from your beam — measure between the mounting points to double-check.";
          }
        }

        function colorName(v) { return optValue(v, isColorOpt); }
        function rebuildSwatches() {
          colorsWrap.innerHTML = "";
          var sig = otherSig(cur), sibs = byColor(sameWidth.filter(function (v) { return otherSig(v) === sig && colorName(v); }), colorName);
          if (sibs.length <= 1) { colorsWrap.hidden = true; return; }
          colorsWrap.hidden = false;
          colorsWrap.appendChild(el("span", "bmf__colors-label", "Colour"));
          swatchRow = el("div", "bmf__swatches");
          sibs.forEach(function (sib) {
            var b = el("button", "bmf__swatch");
            b.type = "button";
            b.setAttribute("aria-label", colorName(sib));
            b.title = colorName(sib);
            var dot = el("span", "bmf__swatch-dot");
            dot.style.background = colorHex(colorName(sib));
            b.appendChild(dot);
            b.appendChild(el("span", null, colorName(sib)));
            if (sib === cur) b.classList.add("is-selected");
            b.addEventListener("click", function () {
              Array.prototype.forEach.call(swatchRow.children, function (c) { c.classList.remove("is-selected"); });
              b.classList.add("is-selected");
              paint(sib);
            });
            swatchRow.appendChild(b);
          });
          colorsWrap.appendChild(swatchRow);
        }

        // width pills (sailboat / cruiser biminis only)
        if (opts.anyWidth && widthVals.length > 1) {
          var wWrap = el("div", "bmf__options");
          wWrap.appendChild(el("span", "bmf__options-label", "Mounting width"));
          var wRow = el("div", "bmf__pills");
          widthVals.forEach(function (wv) {
            var b = el("button", "bmf__pill", widthText(wv));
            b.type = "button";
            b.setAttribute("aria-pressed", String(wv === widthVal));
            b.addEventListener("click", function () {
              var want = colorName(cur), pool = variants.filter(function (v) { return optValue(v, isWidthOpt) === wv; });
              var pick = pool.filter(function (v) { return colorName(v) === want; })[0] || pool[0];
              if (!pick) return;
              Array.prototype.forEach.call(wRow.children, function (c) { c.setAttribute("aria-pressed", "false"); });
              b.setAttribute("aria-pressed", "true");
              sameWidth = pool;
              paint(pick);
              rebuildSwatches();
            });
            wRow.appendChild(b);
          });
          wWrap.appendChild(wRow);
          body.insertBefore(wWrap, optsWrap);
        }

        // option pills (rocket launcher / fishing arch …): one per distinct value in this width
        var pillValues = [], seenVals = {};
        sameWidth.forEach(function (v) { var o = otherOption(v); if (o && !seenVals[o.value]) { seenVals[o.value] = true; pillValues.push(o); } });
        if (!compact && pillValues.length > 1) {
          optsWrap.appendChild(el("span", "bmf__options-label", localWords(pillValues[0].name)));
          var pillRow = el("div", "bmf__pills");
          pillValues.forEach(function (o) {
            var b = el("button", "bmf__pill", localWords(o.value));
            b.type = "button";
            b.setAttribute("aria-pressed", String(o.value === (otherOption(cur) || {}).value));
            b.addEventListener("click", function () {
              var want = colorName(cur), pick = null, i;
              for (i = 0; i < sameWidth.length; i++) {
                var oo = otherOption(sameWidth[i]);
                if (oo && oo.value === o.value && (!want || colorName(sameWidth[i]) === want)) { pick = sameWidth[i]; break; }
              }
              if (!pick) { for (i = 0; i < sameWidth.length; i++) { var o2 = otherOption(sameWidth[i]); if (o2 && o2.value === o.value) { pick = sameWidth[i]; break; } } }
              if (!pick) return;
              Array.prototype.forEach.call(pillRow.children, function (c) { c.setAttribute("aria-pressed", "false"); });
              b.setAttribute("aria-pressed", "true");
              paint(pick);
              rebuildSwatches();
            });
            pillRow.appendChild(b);
          });
          optsWrap.appendChild(pillRow);
        } else {
          optsWrap.remove();
        }

        addBtn = el("button", "bmf__cta bmf__cta--ghost", "Add to cart");
        addBtn.type = "button";
        addBtn.addEventListener("click", function () {
          addBtn.disabled = true; addBtn.textContent = "Adding…";
          fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: [{ id: parseInt(numericId(cur.id), 10), quantity: 1 }].concat(addonsOn().map(function (a) { return { id: parseInt(numericId(a.variant.id), 10), quantity: 1 }; })) }),
          })
            .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
            .then(function () { addBtn.textContent = "Added ✓"; refreshCart(); document.dispatchEvent(new CustomEvent("bmf:added")); setTimeout(function () { addBtn.disabled = false; }, 60); })
            .catch(function () { addBtn.textContent = btnLabel(); addBtn.disabled = false; });
        });
        actions.appendChild(addBtn);

        paint(matched);
        rebuildSwatches();

        // Add-ons, each a tick box + drop-down of every option, added in the same cart call.
        // Support poles on the Classic frames (matching the frame material); rod holder on all.
        function addAddon(key, short, label, handles, mustContain, note) {
          if (!handles.length) return;
          var slot = el("div", "bmf__addon-slot");
          addonWrap.appendChild(slot); // keeps the order even though products load async
          Promise.all(handles.map(fetchProduct)).then(function (prods) {
            var options = [];
            prods.forEach(function (pp) {
              if (!pp) return;
              var vs = ((pp.variants && pp.variants.nodes) || []).filter(function (v) { return v.availableForSale && parseFloat((v.price || {}).amount || 0) > 0; });
              var varying = {}; // option names whose value differs between this product's variants
              if (vs.length > 1) (vs[0].selectedOptions || []).forEach(function (o) {
                if (vs.some(function (v) { return (v.selectedOptions || []).some(function (x) { return x.name === o.name && x.value !== o.value; }); })) varying[o.name] = true;
              });
              vs.forEach(function (v) {
                var vlabel = (v.selectedOptions || []).filter(function (o) { return varying[o.name]; }).map(function (o) { return o.value; }).join(" / ");
                var lbl = prods.length > 1 ? pp.title + (vlabel ? " – " + vlabel : "") : (vlabel || v.title || pp.title);
                if (mustContain && (v.title || "").indexOf(mustContain) < 0) return;
                options.push({ v: v, p: pp, label: lbl.replace(/^Default Title$/, pp.title) });
              });
            });
            if (!options.length) {
              slot.remove();
              var hd = addonWrap.querySelector(".bmf__addon-heading");
              if (hd && !addonWrap.querySelector(".bmf__addon-slot")) addonWrap.remove();
              return;
            }
            var state = { key: key, short: short, on: false, variant: options[0].v };
            addons.push(state);
            var box = el("div", "bmf__addon");
            var lab = el("label", "bmf__addon-head");
            var cb = el("input", "bmf__addon-check"); cb.type = "checkbox";
            var im = new Image(); im.alt = ""; im.loading = "lazy"; im.className = "bmf__addon-img";
            var ab = el("span", "bmf__addon-body");
            var titleRow = el("span", "bmf__addon-title", label);
            ab.appendChild(titleRow);
            var noteEl = null;
            if (note) { // small "i" that opens a note under the add-on (e.g. rod holder load warning)
              var info = el("button", "bmf__addon-info", "i");
              info.type = "button";
              info.setAttribute("aria-label", "More information");
              info.setAttribute("aria-expanded", "false");
              noteEl = el("p", "bmf__addon-note", note);
              noteEl.hidden = true;
              info.addEventListener("click", function (e) {
                e.preventDefault(); e.stopPropagation(); // inside the <label>: never toggles the tick box
                noteEl.hidden = !noteEl.hidden;
                info.setAttribute("aria-expanded", String(!noteEl.hidden));
              });
              titleRow.appendChild(info);
            }
            var sub = el("span", "bmf__addon-sub", "");
            ab.appendChild(sub);
            var pr = el("span", "bmf__addon-price", "");
            lab.appendChild(cb); lab.appendChild(im); lab.appendChild(ab); lab.appendChild(pr);
            box.appendChild(lab);
            if (noteEl) box.appendChild(noteEl);
            var sel = null;
            function show(o) {
              state.variant = o.v;
              var pimg = o.v.image || o.p.featuredImage;
              if (pimg && pimg.url) { im.src = pimg.url; im.hidden = false; } else im.hidden = true;
              sub.textContent = options.length > 1 ? "Choose an option below" : o.label;
              pr.textContent = "+ " + formatPrice(o.v.price);
            }
            if (options.length > 1) {
              sel = el("select", "bmf__addon-select");
              sel.setAttribute("aria-label", label + " option");
              options.forEach(function (o, i) {
                var op = el("option", null, o.label + " (" + formatPrice(o.v.price) + ")");
                op.value = String(i);
                sel.appendChild(op);
              });
              sel.addEventListener("change", function () {
                show(options[parseInt(sel.value, 10)]);
                if (!state.on) { state.on = true; cb.checked = true; box.classList.add("is-on"); }
                paint(cur);
              });
              box.appendChild(sel);
            }
            cb.addEventListener("change", function () { state.on = cb.checked; box.classList.toggle("is-on", state.on); paint(cur); });
            show(options[0]);
            slot.appendChild(box);
          });
        }
        if (!compact && !opts.anyWidth) { // sailboat / cruiser biminis: no add-ons (not in the fitment sheet)
          if (root.dataset.polesAlu !== undefined && /Bow Classic$/.test(line.family)) {
            addAddon("poles", "poles", root.dataset.polesLabel || "Add support poles",
              handleList(line.material === "stainless" ? root.dataset.polesSs : root.dataset.polesAlu));
          }
          if (root.dataset.rodHandles) addAddon("rod", "rod holder", root.dataset.rodLabel || "Add a 360° rod holder", handleList(root.dataset.rodHandles), (root.dataset.rodFilter || "").trim(), (root.dataset.rodNote || "").trim());
        }
        if (!addonWrap.children.length) addonWrap.remove();
        else addonWrap.insertBefore(el("p", "bmf__addon-heading", addonsHeading), addonWrap.firstChild);
      });
      return card;
    }

    function renderEnquire(card, sizeEl, optsWrap, colorsWrap, price, view, code) {
      card.classList.remove("bmf__card--loading");
      card.classList.add("bmf__card--enquire");
      if (optsWrap && optsWrap.parentNode) optsWrap.remove();
      if (colorsWrap && colorsWrap.parentNode) colorsWrap.remove();
      sizeEl.textContent = "Not listed online yet — code " + code;
      price.remove();
      view.textContent = "Enquire";
      view.className = "bmf__cta bmf__cta--ghost";
      view.setAttribute("href", enquireUrl);
    }

    // Measuring diagram pop-up: one native <dialog> per finder, moved to <body> so theme
    // transforms (AOS/parallax) can't trap it, opened with showModal() (top layer).
    var diagDlg = null;
    function openDiagram(src, label) {
      if (!diagDlg) {
        diagDlg = document.createElement("dialog");
        diagDlg.className = "bmf-diagram";
        diagDlg.innerHTML = '<form method="dialog" class="bmf-diagram__bar"><p class="bmf-diagram__title"></p><button class="bmf-diagram__close" aria-label="Close">&times;</button></form><img class="bmf-diagram__img" alt="">';
        diagDlg.addEventListener("click", function (e) { if (e.target === diagDlg) diagDlg.close(); }); // backdrop click
        document.body.appendChild(diagDlg);
      }
      diagDlg.querySelector(".bmf-diagram__title").textContent = "Dimensions: " + label;
      var im = diagDlg.querySelector(".bmf-diagram__img");
      im.src = src; im.alt = "Dimensions diagram for " + label;
      if (diagDlg.showModal) diagDlg.showModal(); else window.open(src, "_blank");
    }

    function renderCards() {
      results.innerHTML = "";
      fabricsList.innerHTML = "";
      fabricsWrap.hidden = true;
      hiddenNote.hidden = true;
      emptyNote.hidden = true;
      if (!current) return;
      syncReset();
      if (current.mode === "sail") { renderSailCards(false); return; }
      var fits = current.fits;
      var all = fittingLines(fits, null);
      var plain = current.mode === "other"; // "Other" boat type: every bimini that fits, unranked
      if (!all.length && !plain) { emptyNote.hidden = false; return; }

      // One card per bimini type; aluminium and stainless of the same type share a card with a
      // switch. Ordered by the sheet's "% of previous customers" (aluminium figure where the
      // type has one); the first card is the recommendation, the rest are alternatives.
      var popular = (!plain && popText && current.popular) || {};
      var families = [], byFam = {};
      all.forEach(function (ln, i) {
        if (!byFam[ln.family]) { byFam[ln.family] = { family: ln.family, i: i, lines: {} }; families.push(byFam[ln.family]); }
        byFam[ln.family].lines[ln.material] = ln;
      });
      families.forEach(function (f) {
        f.mats = ["aluminium", "stainless"].filter(function (m) { return f.lines[m]; });
        f.p = popular[f.lines[f.mats[0]].id] || 0;
      });
      families.sort(function (a, b) { return (b.p - a.p) || (a.i - b.i); });

      families.forEach(function (f, idx) {
        if (!plain && idx === 0) results.appendChild(el("p", "bmf__group-title", headingRec));
        if (!plain && idx === 1) results.appendChild(el("p", "bmf__group-title bmf__group-title--alt", headingAlt));
        var slot = el("div", "bmf__slot");
        function show(mat) {
          var ln = f.lines[mat];
          var card = buildCard(ln, fits[ln.id], {
            measured: current.measured,
            popular: popular[ln.id] || 0,
            recommended: !plain && idx === 0,
            popText: current.popText,
            materials: f.mats,
            onSwitch: show
          });
          if (!plain && idx === 0) card.classList.add("bmf__card--recommended");
          slot.innerHTML = "";
          slot.appendChild(card);
        }
        show(f.mats[0]);
        results.appendChild(slot);
      });

      if (plain) renderSailCards(true);

      // bimini types that don't fit this boat at all, named so the shopper knows why they're absent
      var missing = [];
      DATA.lines.forEach(function (ln) { if (!byFam[ln.family] && missing.indexOf(ln.family) < 0) missing.push(ln.family); });
      if (missing.length) {
        hiddenNote.textContent = "Not shown for this " + (current.measured ? "width" : "boat") + ": " + missing.join(", ") + " — they suit other mounting widths.";
        hiddenNote.hidden = false;
      }

      // replacement fabrics in the same width (shared between aluminium and stainless lines)
      var seenFab = {};
      all.forEach(function (line) {
        if (!line.fabric || seenFab[line.fabric.handle]) return;
        seenFab[line.fabric.handle] = true;
        var suffix = String(fits[line.id]).split("-").pop();
        var fabLine = { id: line.id + "_fabric", family: line.family, material: line.material, bows: line.bows, lengthLabel: line.lengthLabel, handle: line.fabric.handle, title: line.fabric.title, bands: [] };
        fabricsList.appendChild(buildCard(fabLine, line.fabric.codeRoot + "-" + suffix, { compact: true, measured: current.measured }));
      });
      fabricsWrap.hidden = !fabricsList.children.length;
    }

    // Sailboat / Yacht: the sailboat + cruiser biminis, every width (preselected from a typed
    // width when there is one). requireFit (the "Other" list) drops a product with no width that fits.
    function renderSailCards(requireFit) {
      SAIL.forEach(function (handle) {
        var slot = el("div", "bmf__slot");
        var line = { id: "sail_" + handle, family: handle, material: "stainless", handle: handle, title: "",
                     eyebrow: "Stainless steel frame · sailboats, yachts & cruisers" };
        slot.appendChild(buildCard(line, null, { anyWidth: true, width: current.width || 0, requireFit: requireFit,
                                                 onGone: function (sl) { sl.remove(); } }));
        results.appendChild(slot);
      });
      // buildCard's own handlers run first on the same promises, so unfitting cards are gone by now
      Promise.all(SAIL.map(fetchProduct)).then(function () {
        if (current && !results.querySelector(".bmf__card")) emptyNote.hidden = false;
      });
    }

    function hasState() {
      return !!(current || pickedType || (widthInput && widthInput.value) || mfrSel.value);
    }
    function syncReset() { var on = hasState(); resetBtns.forEach(function (b) { b.hidden = !on; }); }
    function resetAll() {
      var viaWidth = widthForm && !widthForm.hidden;
      resetDownstream();
      hideConfigs();
      mfrSel.value = "";
      mdlSel.innerHTML = '<option value="">Select model…</option>'; mdlSel.disabled = true;
      typeBtns.forEach(function (x) { x.setAttribute("aria-checked", "false"); });
      pickedType = ""; pickedMode = "";
      if (widthInput) widthInput.value = "";
      if (widthError) widthError.hidden = true;
      if (measureBox) measureBox.classList.remove("is-next");
      syncReset();
      var target = viaWidth ? widthForm : mfrSel;
      if (viaWidth && restartHint) {
        restartHint.hidden = false;
        restartHint.classList.remove("is-in"); void restartHint.offsetWidth; restartHint.classList.add("is-in");
        var grid = root.querySelector(".bmf__types-grid");
        if (grid) { grid.classList.remove("is-prompt"); void grid.offsetWidth; grid.classList.add("is-prompt"); }
      }
      var r = target.getBoundingClientRect();
      window.scrollTo({ top: window.pageYOffset + r.top - 110, behavior: "smooth" });
      if (!viaWidth) mfrSel.focus({ preventScroll: true });
    }
    resetBtns.forEach(function (b) { b.addEventListener("click", resetAll); });

    function showBoat(rec, mfr, label) {
      var meta = [];
      if (rec.boatType) meta.push(rec.boatType);
      if (rec.length) meta.push(fmtM(rec.length) + " long");
      if (rec.beam) meta.push(fmtM(rec.beam) + " beam");
      boatName.textContent = mfr + " " + (label || modelLabel(rec, mfr)) + (meta.length ? " · " + meta.join(" · ") : "");
      var band = bandLabel(rec.fits);
      boatWidth.textContent = band ? "Your mounting width is about " + band + ", sized from your beam — measure between the mounting points to double-check." : "";
      boatStrip.hidden = false;
    }

    function resetDownstream() {
      results.innerHTML = "";
      fabricsList.innerHTML = "";
      fabricsWrap.hidden = true;
      hiddenNote.hidden = true;
      emptyNote.hidden = true;
      boatStrip.hidden = true;
      current = null;
      syncReset();
    }

    // ---- wire up ----
    mfrSel.addEventListener("change", function () {
      resetDownstream();
      hideConfigs();
      if (!mfrSel.value) { mdlSel.innerHTML = '<option value="">Select model…</option>'; mdlSel.disabled = true; return; }
      populateModels(mfrSel.value);
      syncReset();
    });

    mdlSel.addEventListener("change", function () {
      resetDownstream();
      hideConfigs();
      if (!mdlSel.value) return;
      if (mdlSel._groups) {
        var g = mdlSel._groups[parseInt(mdlSel.value, 10)], sig = fitSig(g.recs[0]);
        var same = g.recs.every(function (r) { return fitSig(r) === sig; });
        if (same) { // every configuration fits the same biminis: no need to ask
          var yrs = g.recs.map(function (r) { return r.years; }).filter(function (y, i, a) { return y && a.indexOf(y) === i; });
          pickBoat(g.recs[0], mfrSel.value, g.label + (yrs.length === 1 ? " (" + yrs[0] + ")" : ""));
        } else showConfigs(g, mfrSel.value);
        return;
      }
      if (!mdlSel._recs) return;
      pickBoat(mdlSel._recs[parseInt(mdlSel.value, 10)], mfrSel.value);
    });

    function parseWidth() {
      var raw = String((widthInput && widthInput.value) || "").replace(",", ".");
      if (IMPERIAL) { // inches by default; feet as 6'5", 6 ft 5 in, or a bare number up to 12
        var ft = /(\d+(?:\.\d+)?)\s*(?:'|\u2032|ft|feet)\s*(?:(\d+(?:\.\d+)?)\s*(?:"|\u2033|in)?)?/i.exec(raw);
        var inch = ft ? parseFloat(ft[1]) * 12 + (parseFloat(ft[2]) || 0) : parseFloat(raw.replace(/[^0-9.]/g, ""));
        if (!ft && inch > 0 && inch <= 12) inch *= 12; // "6.5" = feet
        return inch ? inch * 0.0254 : 0;
      }
      var w = parseFloat(raw.replace(/[^0-9.]/g, ""));
      if (w > 20) w = w / 100; // typed in cm
      return w || 0;
    }
    if (widthInput) widthInput.addEventListener("input", syncReset);

    if (widthToggle && widthForm) {
      widthToggle.addEventListener("click", function () {
        var open = widthForm.hidden;
        widthForm.hidden = !open;
        widthToggle.setAttribute("aria-expanded", String(open));
        if (open && widthInput) widthInput.focus();
      });
      typeBtns.forEach(function (b) {
        b.addEventListener("click", function () {
          var on = b.getAttribute("aria-checked") !== "true";
          typeBtns.forEach(function (x) { x.setAttribute("aria-checked", "false"); });
          b.setAttribute("aria-checked", String(on));
          pickedType = on ? b.getAttribute("data-type") : "";
          pickedMode = on ? (b.getAttribute("data-mode") || "") : "";
          if (restartHint) restartHint.hidden = true;
          syncReset();
          if (pickedMode === "sail") { // no width needed: show the sailboat biminis straight away
            var w0 = parseWidth();
            resetDownstream();
            mdlSel.value = "";
            current = { mode: "sail", width: w0 || 0, measured: !!w0, widthFromProduct: true };
            boatName.textContent = "Sailboat / Yacht / Large Vessel" + (w0 ? " · " + fmtM(w0) + " mounting width" : "");
            boatWidth.textContent = "Choose the width that matches your mounting points" + (w0 ? " — we've preselected the size for your measurement." : ", or enter your mounting width to preselect it.");
            boatStrip.hidden = false;
            renderCards();
            revealResults();
            return;
          }
          if (!on && current && current.mode === "sail") resetDownstream();
          // Point at step 2: the results only appear once a width is entered.
          if (on && measureBox) {
            measureBox.classList.add("is-next");
            if (window.matchMedia("(max-width: 900px)").matches) measureBox.scrollIntoView({ behavior: "smooth", block: "center" });
            if (widthInput && !widthInput.value) widthInput.focus({ preventScroll: true });
          }
        });
      });
      widthForm.addEventListener("submit", function (e) {
        e.preventDefault();
        var w = parseWidth();
        if (!w) { if (widthError) widthError.hidden = false; if (measureBox) measureBox.classList.add("is-next"); widthInput.focus(); return; }
        if (widthError) widthError.hidden = true;
        if (measureBox) measureBox.classList.remove("is-next");
        resetDownstream();
        hideConfigs();
        mdlSel.value = ""; // the results now describe the typed width, not a picked model
        if (pickedMode === "sail") {
          current = { mode: "sail", width: w, measured: true, widthFromProduct: true };
          boatName.textContent = "Sailboat / Yacht / Large Vessel · " + fmtM(w) + " mounting width";
          boatWidth.textContent = "We've preselected the size for your measurement. Please confirm the fit before ordering — or contact us and we'll check it for you.";
          boatStrip.hidden = false;
          renderCards();
          revealResults();
          return;
        }
        var typePop = pickedType && !pickedMode ? popularityForType(pickedType) : null;
        current = {
          mode: pickedMode, width: w,
          fits: fitsFromWidth(w), measured: true, widthFromProduct: true,
          popular: typePop, popText: typePop ? "of " + pickedType.toLowerCase() + " owners chose this bimini" : ""
        };
        boatName.textContent = "Your measurement · " + (pickedType ? pickedType + " · " : "") + fmtM(w) + " mounting width";
        boatWidth.textContent = "Sizes below are matched to the width you entered. Please confirm the fit before ordering — or contact us and we'll check it for you.";
        boatStrip.hidden = false;
        renderCards();
        revealResults();
      });
    }

    if (!dataSrc) return;
    fetch(dataSrc)
      .then(function (r) { return r.json(); })
      .then(function (json) {
        // Lines not sold in this store (section setting, e.g. the 3 Bow stainless in AU) never show.
        var hide = handleList(root.dataset.hideLines);
        if (hide.length) json.lines = json.lines.filter(function (ln) { return hide.indexOf(ln.id) < 0; });
        DATA = json; populateManufacturers();
      })
      .catch(function () {
        emptyNote.hidden = false;
        emptyNote.textContent = "Sorry — the bimini finder could not load. Please refresh or contact us.";
      });
  }

  function boot() { document.querySelectorAll(".bmf").forEach(init); }
  if (document.readyState === "loading") { document.addEventListener("DOMContentLoaded", boot); } else { boot(); }
})();
